import { t, Trans } from "@lingui/macro";
import { zodResolver } from "@hookform/resolvers/zod";
import { FloppyDiskIcon, TranslateIcon } from "@phosphor-icons/react";
import { Button, Form, FormField, FormItem, FormLabel } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { LocaleComboboxPopover } from "@/client/components/locale-combobox";
import { useToast } from "@/client/hooks/use-toast";
import { useUpdateUser, useUser } from "@/client/services/user";

const appearanceSchema = z.object({
  locale: z.string().min(1),
});

type AppearanceFormValues = z.infer<typeof appearanceSchema>;

export const UserAppearanceSettings = () => {
  const { user } = useUser();
  const { updateUser, loading } = useUpdateUser();
  const { toast } = useToast();

  const form = useForm<AppearanceFormValues>({
    resolver: zodResolver(appearanceSchema),
    defaultValues: {
      locale: user?.locale ?? "fr-FR",
    },
  });

  useEffect(() => {
    if (!user) return;

    form.reset({
      locale: user.locale,
    });
  }, [form, user]);

  const handleSubmit = async (values: AppearanceFormValues) => {
    if (!user) return;

    const localeChanged = user.locale !== values.locale;

    if (localeChanged) {
      window.localStorage.setItem("locale", values.locale);

      await updateUser({
        locale: values.locale,
      });
    }

    form.reset(values);

    toast({
      variant: "success",
      title: "Préférences enregistrées",
      description: t`La langue de votre espace a été mise à jour.`,
    });

    if (localeChanged) {
      window.location.reload();
    }
  };

  return (
    <section
      className={cn(
        "rounded-2xl border border-slate-200 bg-white",
        "p-4 shadow-[0_8px_30px_rgba(15,23,42,0.05)]",
        "sm:p-6 lg:p-7",
      )}
    >
      <div className="border-b border-slate-200 pb-5">
        <h2 className="text-base font-bold text-indigo-950">
          <Trans>Langue de l’interface</Trans>
        </h2>

        <p className="mt-1 text-xs leading-5 text-slate-500">
          <Trans>Choisissez la langue des menus et des messages.</Trans>
        </p>
      </div>

      <Form {...form}>
        <form className="mt-6" onSubmit={form.handleSubmit(handleSubmit)}>
          <FormField
            name="locale"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <div className="flex items-start gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-emerald-50 text-emerald-600">
                    <TranslateIcon aria-hidden size={20} weight="duotone" />
                  </span>

                  <div className="min-w-0 flex-1">
                    <FormLabel className="text-xs font-bold text-indigo-950">
                      Langue de l’application
                    </FormLabel>

                    <p className="mb-3 mt-1 text-[11px] leading-5 text-slate-500">
                      Les menus et les messages seront affichés dans cette langue.
                    </p>

                    <div className="w-full max-w-md">
                      <LocaleComboboxPopover value={field.value} onValueChange={field.onChange} />
                    </div>
                  </div>
                </div>
              </FormItem>
            )}
          />

          <div className="mt-7 flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={!form.formState.isDirty || loading}
              onClick={() => form.reset()}
              className="rounded-xl text-xs"
            >
              Annuler
            </Button>

            <Button
              type="submit"
              disabled={!form.formState.isDirty || loading}
              className="rounded-xl bg-indigo-600 text-xs hover:bg-indigo-700"
            >
              <FloppyDiskIcon aria-hidden className="mr-2" size={16} />

              {loading ? "Enregistrement…" : "Enregistrer les préférences"}
            </Button>
          </div>
        </form>
      </Form>
    </section>
  );
};
